import { useCallback, useState, useEffect, useRef } from "react";
import { supabase } from "@/integrations/supabase/client";
import { useVoiceInputWhisper, type WhisperOwnerGuard } from "@/hooks/useVoiceInputWhisper";
import { useQuotaErrorSurface } from "@/hooks/useQuotaErrorSurface";
import { agendaOwnerSession } from "@/lib/agendaExecutor";
import { agendaReminderOffsets } from "@/lib/agendaCaptureData";
import { edgeLocaleFor, type Lang } from "@/i18n/locale";

// Voice-first calendar capture orchestrator.
// Wraps `useVoiceInputWhisper` (recording + Whisper STT) and chains a call
// to the `agenda-extract` edge function so consumers get a single state
// machine: idle -> recording -> transcribing -> extracting -> ready.

export type AgendaCaptureStatus =
  | "idle"
  | "recording"
  | "transcribing"
  | "extracting"
  | "ready"
  | "error";

export interface ExtractedAgendaEvent {
  title: string;
  start_iso: string;
  end_iso: string | null;
  duration_minutes: number | null;
  location: string | null;
  attendees: string[];
  notes: string | null;
  confidence: "high" | "medium" | "low";
  tag: "Focus" | "Meeting" | "Personal";
  reminder_minutes: number | null;
  reminders?: number[];
}

export interface UseAgendaVoiceCaptureOptions {
  lang: Lang;
  ownerId?: string;
  onTranscriptReady?: (transcript: string) => void;
  onEventsExtracted?: (events: ExtractedAgendaEvent[], rawTranscript: string) => void;
}

export interface UseAgendaVoiceCaptureReturn {
  status: AgendaCaptureStatus;
  recordingSeconds: number;
  amplitude: number;
  lastTranscript: string | null;
  lastEvents: ExtractedAgendaEvent[] | null;
  error: string | null;
  start: () => Promise<void>;
  stop: () => Promise<void>;
  reset: () => void;
  supported: boolean;
}



export function useAgendaVoiceCapture(
  options: UseAgendaVoiceCaptureOptions,
): UseAgendaVoiceCaptureReturn {
  const { lang, onTranscriptReady, onEventsExtracted } = options;

  // Reuse the canonical Whisper recorder — iOS fix, amplitude RAF, timer,
  // MediaRecorder lifecycle live there. Don't duplicate.
  const stt = useVoiceInputWhisper(lang);
  const surfaceQuota = useQuotaErrorSurface();
  const mounted = useRef(false), owner = useRef<string | null | undefined>(options.ownerId), generation = useRef(0), recording = useRef<WhisperOwnerGuard | null>(null);
  const stopRecorder = useRef(stt.stopListening); stopRecorder.current = stt.stopListening;

  const [status, setStatus] = useState<AgendaCaptureStatus>("idle");
  const [lastTranscript, setLastTranscript] = useState<string | null>(null);
  const [lastEvents, setLastEvents] = useState<ExtractedAgendaEvent[] | null>(null);
  const [error, setError] = useState<string | null>(null);

  const reset = useCallback(() => {
    generation.current++; const old = recording.current; recording.current = null;
    if (old) void stopRecorder.current(old);
    setStatus("idle");
    setLastTranscript(null);
    setLastEvents(null);
    setError(null);
  }, []);

  useEffect(() => {
    mounted.current = true;
    const { data: { subscription } } = supabase.auth.onAuthStateChange((_event, session) => {
      const next = session?.user.id ?? null; if (owner.current === next) return;
      owner.current = next; generation.current++; const old = recording.current; recording.current = null;
      reset(); if (old) void stopRecorder.current(old);
    });
    const epoch = generation;
    return () => { mounted.current = false; epoch.current++; const old = recording.current; recording.current = null; if (old) void stopRecorder.current(old); subscription.unsubscribe(); };
  }, [reset]);
  const start = useCallback(async () => {
    const epoch = generation.current;
    const current = () => mounted.current && epoch === generation.current;
    if (!current()) return;
    setError(null); setLastTranscript(null); setLastEvents(null);
    try {
      const user = await supabase.auth.getUser(); if (!current()) return;
      const uid = user.data.user?.id;
      if (user.error || !uid || options.ownerId && options.ownerId !== uid || owner.current !== undefined && owner.current !== uid) throw new Error("invalid_session");
      owner.current = uid;
      const guard = { ownerId: uid, isCurrent: () => current() && owner.current === uid };
      await agendaOwnerSession(uid, () => { if (!guard.isCurrent()) throw new Error("invalid_session"); });
      if (!guard.isCurrent()) return;
      recording.current = guard; await stt.startListening(guard);
      if (guard.isCurrent()) setStatus("recording");
    } catch (e) { if (current()) { console.warn("[Agenda voice] capture unavailable"); setError(e instanceof Error ? e.message : "mic error"); setStatus("error"); } }
  }, [stt, options.ownerId]);

  const stop = useCallback(async () => {
    const guard = recording.current;
    if (!guard || !guard.isCurrent()) return;
    const assert = () => { if (!guard.isCurrent()) throw new Error("invalid_session"); };
    try {
      setStatus("transcribing");
      const transcript = (await stt.stopListening(guard)).trim(); assert();
      if (!transcript) { setError("empty transcript"); setStatus("error"); return; }
      setLastTranscript(transcript); onTranscriptReady?.(transcript); assert();
      setStatus("extracting");
      const session = await agendaOwnerSession(guard.ownerId, assert); assert();
      const { data, error: invokeErr } = await supabase.functions.invoke("agenda-extract", { headers: { Authorization: "Bearer " + session.access_token }, body: {
        transcript, language: edgeLocaleFor(lang) ?? "en", user_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone || "UTC", reference_date_iso: new Date().toISOString(),
      } }); assert(); await agendaOwnerSession(guard.ownerId, assert); assert();
      if (invokeErr) { if (await surfaceQuota(invokeErr)) { assert(); setStatus("idle"); return; } assert(); setError(invokeErr.message || "agenda-extract failed"); setStatus("error"); return; }
      if (!Array.isArray(data?.events) || data.events.length > 1) throw new Error("invalid_agenda_extract");
      const events = data.events as ExtractedAgendaEvent[];
      for (const event of events) { if (!event || typeof event.title !== "string" || typeof event.start_iso !== "string" || !Array.isArray(event.attendees) || event.attendees.some(value => typeof value !== "string")) throw new Error("invalid_agenda_extract");
        event.reminders = agendaReminderOffsets(event.reminders, event.reminder_minutes); }
      assert(); setLastEvents(events); onEventsExtracted?.(events, transcript); assert(); setStatus("ready");
    } catch (e) { if (guard.isCurrent()) { console.warn("[Agenda voice] extraction unavailable"); setError(e instanceof Error ? e.message : "capture failed"); setStatus("error"); } }
  }, [stt, lang, onTranscriptReady, onEventsExtracted, surfaceQuota]);

  return {
    status,
    recordingSeconds: stt.recordingSeconds,
    amplitude: stt.amplitude,
    lastTranscript,
    lastEvents,
    error,
    start,
    stop,
    reset,
    supported: stt.supported,
  };
}
